// src/components/FlowsList.js - Lista de Fluxos com Importar/Exportar
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';

function FlowsList() {
  const navigate = useNavigate();
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);

  const getUserId = () => {
    const user = JSON.parse(localStorage.getItem('zaptime_user') || '{}');
    return user.id;
  };

  const getHeaders = () => ({
    'Content-Type': 'application/json',
    'X-User-Id': getUserId()
  });

  useEffect(() => {
    loadFlows();
  }, []);

  const loadFlows = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/flows', {
        headers: getHeaders()
      });
      const data = await response.json();
      
      if (data.success) {
        setFlows(data.flows);
      }
    } catch (error) {
      console.error('Erro ao carregar fluxos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (flowId) => {
    try {
      const response = await fetch(`/api/flows/${flowId}/activate`, {
        method: 'POST',
        headers: getHeaders()
      });
      const data = await response.json();
      
      if (data.success) {
        alert('✅ Fluxo ativado!');
        loadFlows();
      } else {
        alert('❌ ' + data.message);
      }
    } catch (error) {
      alert('❌ Erro ao ativar fluxo');
    }
  };

  const handleDeactivate = async (flowId) => {
    try {
      const response = await fetch(`/api/flows/${flowId}/deactivate`, {
        method: 'POST',
        headers: getHeaders()
      });
      const data = await response.json();
      
      if (data.success) {
        alert('⏸️ Fluxo desativado!');
        loadFlows();
      } else {
        alert('❌ ' + data.message);
      }
    } catch (error) {
      alert('❌ Erro ao desativar fluxo');
    }
  };

  const handleDelete = async (flowId, flowName) => {
    if (!window.confirm(`Tem certeza que deseja deletar o fluxo "${flowName}"?`)) {
      return;
    }
    
    try {
      const response = await fetch(`/api/flows/${flowId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      const data = await response.json();
      
      if (data.success) {
        alert('🗑️ Fluxo deletado!');
        loadFlows();
      } else {
        alert('❌ ' + data.message);
      }
    } catch (error) {
      alert('❌ Erro ao deletar fluxo');
    }
  };

  const handleEdit = (flowId) => {
    navigate(`/flow-editor/${flowId}`);
  };

  const handleNew = () => {
    navigate('/flow-editor');
  };

  const handleExportFlow = async (flowId, flowName) => {
    try {
      const response = await fetch(`/api/flows/${flowId}/export`, {
        headers: getHeaders()
      });
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${flowName}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      alert(`✅ Fluxo "${flowName}" exportado!`);
    } catch (error) {
      alert('❌ Erro ao exportar fluxo');
      console.error(error);
    }
  };

  const handleExportAll = async () => {
    try {
      const response = await fetch('/api/flows/export-all/download', {
        headers: getHeaders()
      });
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `todos-fluxos-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      alert(`✅ ${flows.length} fluxo(s) exportado(s)!`);
    } catch (error) {
      alert('❌ Erro ao exportar fluxos');
      console.error(error);
    }
  };

  const handleImportFlow = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    try {
      setImporting(true);
      const text = await file.text();
      const flowData = JSON.parse(text);
      
      if (flowData.flows && Array.isArray(flowData.flows)) {
        const response = await fetch('/api/flows/import-multiple', {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({ flows: flowData.flows })
        });
        const data = await response.json();
        
        if (data.success) {
          alert(`✅ ${data.imported.length} fluxo(s) importado(s)!`);
          loadFlows();
        } else {
          alert('❌ ' + data.message);
        }
      } else {
        const response = await fetch('/api/flows/import', {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify(flowData)
        });
        const data = await response.json();
        
        if (data.success) {
          alert(`✅ Fluxo "${data.flow.name}" importado!`);
          loadFlows();
        } else {
          alert('❌ ' + data.message);
        }
      }
    } catch (error) {
      alert('❌ Erro ao importar: Arquivo JSON inválido');
      console.error(error);
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  if (loading) {
    return <div style={styles.loading}>⏳ Carregando...</div>;
  }

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>📋 Meus Fluxos</h1>
      </header>

      <nav style={styles.nav}>
        <Link to="/" style={styles.navLink}>🏠 Dashboard</Link>
        <Link to="/flows" style={{...styles.navLink, ...styles.navLinkActive}}>📋 Meus Fluxos</Link>
      </nav>

      <main style={styles.main}>
        <div style={styles.actionsBar}>
          <button 
            onClick={handleNew}
            style={{...styles.btn, ...styles.btnPrimary}}
          >
            ➕ Novo Fluxo
          </button>
          
          <div style={styles.importExportButtons}>
            <label style={{...styles.btn, ...styles.btnImport}}>
              📥 Importar
              <input
                type="file"
                accept=".json"
                onChange={handleImportFlow}
                style={styles.fileInput}
                disabled={importing}
              />
            </label>
            
            <button 
              onClick={handleExportAll}
              style={{...styles.btn, ...styles.btnExport}}
              disabled={flows.length === 0}
            >
              📤 Exportar Todos
            </button>
          </div>
        </div>

        {flows.length === 0 ? (
          <div style={styles.emptyState}>
            <p style={styles.emptyText}>📭 Nenhum fluxo criado ainda.</p>
            <p style={styles.emptyHint}>Clique em "➕ Novo Fluxo" para começar!</p>
          </div>
        ) : (
          <div style={styles.flowsGrid}>
            {flows.map(flow => (
              <div key={flow.id} style={styles.flowCard}>
                <div style={styles.flowHeader}>
                  <h3 style={styles.flowName}>{flow.name}</h3>
                  {flow.active && <span style={styles.activeBadge}>✅ ATIVO</span>}
                </div>
                
                <div style={styles.flowInfo}>
                  <p style={styles.flowTrigger}>
                    🎯 Gatilho: <strong>"{flow.trigger}"</strong>
                  </p>
                  <p style={styles.flowBlocks}>
                    📦 Blocos: <strong>{flow.blocks.length}</strong>
                  </p>
                </div>

                <div style={styles.flowActions}>
                  <button
                    onClick={() => handleEdit(flow.id)}
                    style={{...styles.btnSmall, ...styles.btnEdit}}
                  >
                    ✏️ Editar
                  </button>
                  
                  {flow.active ? (
                    <button
                      onClick={() => handleDeactivate(flow.id)}
                      style={{...styles.btnSmall, ...styles.btnDeactivate}}
                    >
                      ⏸️ Desativar
                    </button>
                  ) : (
                    <button
                      onClick={() => handleActivate(flow.id)}
                      style={{...styles.btnSmall, ...styles.btnActivate}}
                    >
                      ▶️ Ativar
                    </button>
                  )}
                  
                  <button
                    onClick={() => handleExportFlow(flow.id, flow.name)}
                    style={{...styles.btnSmall, ...styles.btnExportSingle}}
                  >
                    📥 Exportar
                  </button>
                  
                  <button
                    onClick={() => handleDelete(flow.id, flow.name)}
                    style={{...styles.btnSmall, ...styles.btnDelete}}
                  >
                    🗑️ Deletar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0a0a0a',
    color: '#e0e0e0',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  header: {
    backgroundColor: '#1a1a1a',
    padding: '20px',
    borderBottom: '1px solid #333',
    textAlign: 'center'
  },
  title: {
    margin: 0,
    fontSize: '24px',
    color: '#fff'
  },
  nav: {
    display: 'flex',
    gap: '10px',
    padding: '15px 20px',
    backgroundColor: '#0f0f0f',
    borderBottom: '1px solid #222'
  },
  navLink: {
    padding: '10px 20px',
    backgroundColor: '#1a1a1a',
    color: '#aaa',
    textDecoration: 'none',
    borderRadius: '6px',
    border: '1px solid #333',
    transition: 'all 0.2s'
  },
  navLinkActive: {
    backgroundColor: '#2563eb',
    color: '#fff',
    border: '1px solid #3b82f6'
  },
  main: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  loading: {
    textAlign: 'center',
    padding: '50px',
    fontSize: '18px',
    color: '#888'
  },
  actionsBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '10px'
  },
  importExportButtons: {
    display: 'flex',
    gap: '10px'
  },
  btn: {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'inline-block'
  },
  btnPrimary: {
    backgroundColor: '#10b981',
    color: '#fff'
  },
  btnImport: {
    backgroundColor: '#3b82f6',
    color: '#fff',
    position: 'relative',
    cursor: 'pointer'
  },
  btnExport: {
    backgroundColor: '#8b5cf6',
    color: '#fff'
  },
  fileInput: {
    display: 'none'
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    backgroundColor: '#1a1a1a',
    borderRadius: '12px',
    border: '1px solid #333'
  },
  emptyText: {
    fontSize: '18px',
    color: '#888',
    marginBottom: '10px'
  },
  emptyHint: {
    fontSize: '14px',
    color: '#666'
  },
  flowsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '20px'
  },
  flowCard: {
    backgroundColor: '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '12px',
    padding: '20px',
    transition: 'transform 0.2s, box-shadow 0.2s'
  },
  flowHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px'
  },
  flowName: {
    margin: 0,
    fontSize: '18px',
    color: '#fff'
  },
  activeBadge: {
    padding: '4px 8px',
    backgroundColor: '#10b981',
    color: '#fff',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600'
  },
  flowInfo: {
    marginBottom: '15px',
    paddingBottom: '15px',
    borderBottom: '1px solid #2a2a2a'
  },
  flowTrigger: {
    margin: '5px 0',
    fontSize: '14px',
    color: '#aaa'
  },
  flowBlocks: {
    margin: '5px 0',
    fontSize: '14px',
    color: '#aaa'
  },
  flowActions: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap'
  },
  btnSmall: {
    padding: '8px 12px',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  btnEdit: {
    backgroundColor: '#3b82f6',
    color: '#fff'
  },
  btnActivate: {
    backgroundColor: '#10b981',
    color: '#fff'
  },
  btnDeactivate: {
    backgroundColor: '#f59e0b',
    color: '#fff'
  },
  btnExportSingle: {
    backgroundColor: '#8b5cf6',
    color: '#fff'
  },
  btnDelete: {
    backgroundColor: '#ef4444',
    color: '#fff'
  }
};

export default FlowsList;
